import moment from 'moment';
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Alert,
  Button,
  Input,
  Popover,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
// API
import { GET_AUDIT_LOGS_ADMIN } from 'api/auditLogs/queries';
// Types
import {
  GetAuditLogsAdmin,
  GetAuditLogsAdmin_getAuditLogsAdmin_items,
  GetAuditLogsAdminVariables,
} from 'api/auditLogs/types/GetAuditLogsAdmin';
import {
  AuditLogType,
  CbBalanceTransactionType,
  InterviewAuditLogAction,
  SortDirection,
  VideoEditRequestAuditLogAction,
} from 'api/graphql-global-types';
// Constants
import {
  CB_TEAM_AUDIT_ACTIONS,
  COUNTER_OFFER_AUDIT_ACTIONS,
  DELIVERABLE_REVIEW_AUDIT_ACTIONS,
  DESIGN_REQUEST_AUDIT_ACTIONS,
  PROMO_CODE_AUDIT_ACTIONS,
} from 'constants/auditLogs';
// Hooks
import { useGetAdmins } from 'hooks/useGetAdmins';
import { useDebouncedValue } from 'hooks';
// Helpers
import {
  actionGroup,
  buildAuditLogsInput,
  getAction,
  getPerformer,
} from 'helpers/auditLogs';
// UI
import Table, {
  SortedInfo,
  TableColumnFilterItem,
  TableFilter,
} from 'ui/Table';

const { Title, Text } = Typography;

type AuditLogEntry = GetAuditLogsAdmin_getAuditLogsAdmin_items;
type AuditLogsOrderBy = 'createdAt';

const ACTION_FILTERS: TableColumnFilterItem[] = [
  actionGroup('Interview', Object.values(InterviewAuditLogAction)),
  actionGroup(
    'Video Edit Request',
    Object.values(VideoEditRequestAuditLogAction)
  ),
  actionGroup('Design Request', DESIGN_REQUEST_AUDIT_ACTIONS),
  actionGroup('Campaign Balance', Object.values(CbBalanceTransactionType)),
  actionGroup('Promo Code', PROMO_CODE_AUDIT_ACTIONS),
  actionGroup('Counter Offer', COUNTER_OFFER_AUDIT_ACTIONS),
  actionGroup('Deliverable Review', DELIVERABLE_REVIEW_AUDIT_ACTIONS),
  actionGroup('CB Team', CB_TEAM_AUDIT_ACTIONS),
];

const TYPE_COLORS: Record<AuditLogType, string> = {
  [AuditLogType.Interview]: 'blue',
  [AuditLogType.VideoEditRequest]: 'purple',
  [AuditLogType.DesignRequest]: 'magenta',
  [AuditLogType.CampaignBalance]: 'gold',
  [AuditLogType.PromoCode]: 'green',
  [AuditLogType.CounterOffer]: 'orange',
  [AuditLogType.DeliverableReview]: 'cyan',
  [AuditLogType.CbTeam]: 'geekblue',
  [AuditLogType.TicketOrder]: 'volcano',
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const AuditLogs: React.FC = () => {
  const [pageSize, setPageSize] = useState<number>(10);
  const [sort, setSortInfo] = useState<SortedInfo<AuditLogsOrderBy>>({
    order: SortDirection.DESC,
    key: 'createdAt',
  });
  const [searchFilters, setSearchFilters] = useState<TableFilter[]>([]);
  const [emailInput, setEmailInput] = useState<string>('');

  const debouncedEmail = useDebouncedValue(emailInput.trim(), 1000);

  // Cursors only belong to the query they were issued for; keying them makes a filter change start from page one in the same render.
  const queryKey = JSON.stringify({
    searchFilters,
    sort,
    pageSize,
    debouncedEmail,
  });
  const [cursors, setCursors] = useState<{ key: string; stack: string[] }>({
    key: queryKey,
    stack: [],
  });
  const cursorStack = cursors.key === queryKey ? cursors.stack : [];

  const { data: adminData } = useGetAdmins();

  const { data, loading, error } = useQuery<
    GetAuditLogsAdmin,
    GetAuditLogsAdminVariables
  >(GET_AUDIT_LOGS_ADMIN, {
    variables: {
      input: buildAuditLogsInput({
        searchFilters,
        direction: sort.order,
        limit: pageSize,
        startAfter: cursorStack[cursorStack.length - 1],
        performedByEmail: debouncedEmail || undefined,
      }),
    },
    fetchPolicy: 'cache-and-network',
  });

  const nextCursor = data?.getAuditLogsAdmin.startAfter;

  const handleNextPage = (): void => {
    if (nextCursor) {
      setCursors({ key: queryKey, stack: [...cursorStack, nextCursor] });
    }
  };

  const handlePrevPage = (): void => {
    setCursors({ key: queryKey, stack: cursorStack.slice(0, -1) });
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
      sorterType: 'date',
      withDateRangeFilter: true,
      render: (value: string) =>
        moment(value).local().format('MM/DD/YYYY, h:mm a'),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'types',
      width: 160,
      withCheckboxFilters: true,
      filters: Object.values(AuditLogType).map((type) => ({
        text: String(type),
        value: String(type),
      })),
      render: (value: AuditLogType) => (
        <Tag color={TYPE_COLORS[value]}>{value}</Tag>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'type',
      key: 'actions',
      width: 200,
      withCheckboxFilters: true,
      filters: ACTION_FILTERS,
      render: (_: string, record: AuditLogEntry) => getAction(record),
    },
    {
      title: 'Performed by',
      dataIndex: 'performedByEmail',
      key: 'performedById',
      width: 260,
      withAdminSearch: true,
      adminSearchData: adminData,
      render: (_: string, record: AuditLogEntry) => {
        const { email, name, role } = getPerformer(record);
        if (!record.performedById && !email && !name) {
          return <Text type="secondary">System</Text>;
        }
        return (
          <div>
            <div>
              {name || email || '-'}
              {role && <Tag style={{ marginLeft: 8 }}>{role}</Tag>}
            </div>
            {name && email && <Text type="secondary">{email}</Text>}
          </div>
        );
      },
    },
    {
      title: 'Target',
      dataIndex: 'targetId',
      key: 'targetId',
      width: 220,
      withSearch: true,
      forceSearchRender: true,
      render: (_: string, record: AuditLogEntry) => {
        if (!record.targetId) {
          return '-';
        }
        return (
          <div>
            {record.targetType && <Text>{record.targetType}</Text>}
            <div>
              <Text type="secondary" copyable={{ text: record.targetId }}>
                {record.targetId.slice(0, 8)}…
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Details',
      dataIndex: 'data',
      key: 'data',
      width: 90,
      render: (value: AuditLogEntry['data']) => {
        if (!value || !Object.keys(value).length) {
          return '-';
        }
        return (
          <Popover
            content={
              <pre style={{ maxWidth: 480, maxHeight: 400, overflow: 'auto' }}>
                {JSON.stringify(value, null, 2)}
              </pre>
            }
            trigger="click"
          >
            <Typography.Link>View</Typography.Link>
          </Popover>
        );
      },
    },
  ];

  return (
    <div>
      <Title level={2}>Audit Logs</Title>

      <Space align="center" style={{ marginBottom: 16 }}>
        <Input
          style={{ width: 320 }}
          onChange={(event) => setEmailInput(event.target.value)}
          placeholder="Search by performer email"
          value={emailInput}
          allowClear
        />
        <Tooltip title="Matches the email of the user who performed the action, including non-admin users (e.g. athletes and brands).">
          <InfoCircleOutlined />
        </Tooltip>
      </Space>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Error loading audit logs: ${error.message}`}
        />
      )}

      <Table<AuditLogEntry, AuditLogsOrderBy>
        columns={columns}
        data={data?.getAuditLogsAdmin.items || []}
        scroll={{ x: 1100 }}
        loading={loading}
        pagination={false}
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
        sortInfo={sort}
        setSortInfo={setSortInfo}
      />

      <Space
        style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}
      >
        <Select<number>
          value={pageSize}
          onChange={setPageSize}
          options={PAGE_SIZE_OPTIONS.map((size) => ({
            value: size,
            label: `${size} / page`,
          }))}
        />
        <Button
          icon={<LeftOutlined />}
          onClick={handlePrevPage}
          disabled={loading || !cursorStack.length}
        >
          Previous
        </Button>
        <Button onClick={handleNextPage} disabled={loading || !nextCursor}>
          Next <RightOutlined />
        </Button>
      </Space>
    </div>
  );
};

export default AuditLogs;
